/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import { HeadlineView } from '../components/Elements/SingleElements/HeadlineView';
import React, { ReactElement } from 'react';
import { DescriptionView } from '../components/Elements/SingleElements/DescriptionView';
import { ImageSliderView } from '../components/Elements/SingleElements/ImageSlider/ImageSliderView';
import { ExpandableView } from '../components/Elements/SingleElements/Expandable/ExpandableView';
import { SingleChoiceView } from '../components/Elements/SingleElements/Choice/SingleChoiceView';
import { MultipleChoiceView } from '../components/Elements/SingleElements/Choice/MultipleChoiceView';
import { TextFieldView } from '../components/Elements/SingleElements/TextFieldView';
import { CreateEntryOptionsView } from '../components/Elements/SingleElements/CreateEntryOptionsView';
import { Element } from '../types/elements/Element';
import { ClassNameMap } from 'notistack';
import { Image } from '../types/elements/Image';

export enum ELEMENTS_TYPE {
	MODULE,
	ENTRY,
}

/**
 * Creates and returns the appropriate React element based on the element type.
 * Handles all supported element types including text, images, choices, and create-entry options.
 *
 * @param element - The element configuration object
 * @param classes - Material-UI style classes
 * @param identifier - Unique identifier for the element
 * @param last - Whether this is the last element
 * @param type - The context type (MODULE or ENTRY)
 * @param selectedOptions - Selected options object for create-entry elements (maps group title/ID to values)
 * @returns A React element to be rendered
 * @author Julien Tietke <j.tietke@tj-labs.de>
 */
export const createElement = (
	element: Element,
	classes: ClassNameMap,
	identifier: number,
	last: boolean,
	type: ELEMENTS_TYPE,
	selectedOptions?: Record<string, string[] | string>
): ReactElement => {
	const text = element.text as string;
	const name = element.name as string;
	const info = element.information as string;
	const images = element.images as Image[];

	switch (element.type) {
		case 'headline':
			return (
				<HeadlineView key={identifier} className={classes.headline} styling={classes.headlineStyling} headline={text} />
			);
		case 'description':
			return <DescriptionView key={identifier} description={text} className={classes.description} />;
		case 'image-slider':
			return <ImageSliderView key={identifier} images={images} className={classes.slider} />;
		case 'expandable':
			return <ExpandableView key={identifier} name={name} info={info} images={images} last={last} />;
		case 'single-choice':
			return (
				<SingleChoiceView
					key={identifier}
					options={element.text_options}
					type={type}
					selectedOption={element.selected_option}
				/>
			);
		case 'multiple-choice':
			return (
				<MultipleChoiceView
					key={identifier}
					textOptions={element.text_options}
					imageOptions={element.image_options}
					selectedOptions={element.selected_options}
					type={type}
				/>
			);
		case 'text-field':
			return (
				<TextFieldView
					key={identifier}
					className={classes.textField}
					styling={classes.textFieldContent}
					studentInput={element.student_input}
				/>
			);
		case 'create-entry':
			return (
				<CreateEntryOptionsView
					key={identifier}
					optionsGroups={element.optionsGroups || []}
					selectedOptionsObject={selectedOptions}
					type={type}
				/>
			);
		default:
			return <></>;
	}
};
